import {IDisplayedField} from "./models/Settings";
import {FontWeights, getTheme, IButtonStyles, mergeStyleSets} from "@fluentui/react";

export const WORKSPACE_NAME = "workspace_name";
/** The one icon that means "prepared cart" wherever the UI links to that context (pills, omnibox, suggestions). */
export const PREPARED_CART_ICON = 'ShoppingCartSolid';
export const DISPLAY_FORMAT_STORAGE_KEY = "display_format";

export const EXTENSION_TYPES = [
    "Import",
    "Export",
    "Webhook",
    "Realtime"
];

export enum ExtensionType {
    Import = "import",
    Export = "export",
    Webhook = "webhook",
    Realtime = "realtime",
    Recommendation = "recommendation",
    Procurement = "procurement",
    StorefrontOpportunity = "storefrontOpportunity",
    Locations = "locations",
    Customer = "customer",
    StorefrontCustomer = "storefrontCustomer",
    Tax = "tax",
    SavedSearchBulkExport = "savedSearchBulkExport",
}

// Sentinel value for the "Adaptive Catalog Locations" static option in the Locations
// extension dropdown (SubExtensionDropdown). Selecting it tells the API to serve
// locations from Adaptive Catalog's own location data instead of an installed extension.
export const AC_LOCATIONS_EXTENSION = 'AC_LOCATIONS';

interface IProductFieldNames {
    [key: string]: string,
}

export const PRODUCT_FIELDS: IProductFieldNames = {
    'costDisplay': 'Cost',
    'stockDisplay': 'Availability',
    'imageDisplay': 'Image',
    'manufacturer': 'Manufacturer',
    'manufacturerPartNumber': 'MFP',
    'msrp': 'MSRP',
    'name': 'Name',
    'segment': 'Segment',
    'category': 'Category',
}

export const DEFAULT_DISPLAY_FIELDS: IDisplayedField[] = [
    {
        id: 1,
        field: 'imageDisplay',
        size: 1,
    },
    {
        id: 2,
        field: 'manufacturer',
        size: 2,
    },
    {
        id: 3,
        field: 'manufacturerPartNumber',
        size: 2,
    },
    {
        id: 4,
        field: 'name',
        size: 5,
    },
    {
        id: 5,
        field: 'stockDisplay',
        size: 2,
    },
    {
        id: 6,
        field: 'costDisplay',
        size: 2,
    }
]

export const MULTI_EDIT_FIELDS = [
    'manufacturer'
];

export const MANUFACTURER = 'manufacturer';

export const SEGMENT = 'segment';

export const CATEGORY = 'category';

export const PLACEHOLDER_IMAGE = '/no-image.png';
/** The icon that means "product bundle" across the app (search strip, workspace, omnibox). */
export const BUNDLE_ICON = 'Package';

// 'details' is intentionally omitted — the Details action column is always appended by
// ResultTable, so it isn't a user-customizable column.
export const SEARCHCOLUMNS: string[] = ["favorite", "image", "manufacturer", "mfp", "name", "availability", "cost"];

export const WORKSPACECOLUMNS: string[] = ["sortOrder", "image", "type", "name", "availability", "cost", "presentedPrice", "quantity", "actions"];

export const EXPORTABLEPROCUREMENTCOLUMNS: string[] = ["name", "manufacturer", "manufacturerPartNumber", "source", "quantity", "id", "userName", "productId", "workspace",
    "workspaceName", "cost", "vendorPartNumber", "company", "order", "stock", "requirementNotes", "externalItemId", 
    "requestedCarrier", "orderedQuantity", "backOrderedQuantity", "confirmedQuantity", "confirmedCost", "orderLineNumber"];

export const PREPAREORDERITEMCOLUMNS: string[] = ["details", "name", "manufacturer", "mfp", "vpn", "prepareQuantity", "stock", "cost", "userName", "workspace"];
export const ORDERITEMCOLUMNS: string[] = ["name", "manufacturer", "mfp", "vpn", "quantity", "stock", "cost", "userName", "workspace"];
export const DIRECTIMPORTCOLUMNS: string[] = ["bundle", "manufacturerPartNumber", "manufacturer", "vendorPartNumber", "cost", "price", "name", "description", "quantity", "opportunity", "salesOrder", "company", "vendor", "businessUnit", "location", "productClass", "purchaseDate", "status", "isAcProduct"];
export const DEFAULTDIRECTIMPORTCOLUMNS: string[] =["isAcProduct", "bundle", "manufacturerPartNumber", "manufacturer", "cost", "price", "name", "description", "quantity", "opportunity", "purchaseDate",]

const theme = getTheme();
export const MODAL_STYLE = mergeStyleSets({
    container: {
        display: 'flex',
        flexFlow: 'column nowrap',
        alignItems: 'stretch',
    },
    header: [
        theme.fonts.xLargePlus,
        {
            flex: '1 1 auto',
            borderTop: '4px solid #789c2f',
            color: theme.palette.neutralPrimary,
            display: 'flex',
            alignItems: 'center',
            fontWeight: FontWeights.semibold,
            padding: '12px 12px 14px 24px',
        },
    ],
    body: {
        flex: '4 4 auto',
        padding: '0 24px 24px 24px',
        overflowY: 'unset',
        selectors: {
            p: {margin: '14px 0'},
            'p:first-child': {marginTop: 0},
            'p:last-child': {marginBottom: 0},
        },
    },
    footer: {
        flex: '4 4 auto',
        padding: '0 24px 24px 24px',
        overflowY: 'unset',
        textAlign: 'right',
        selectors: {
            p: {margin: '14px 0'},
            'p:first-child': {marginTop: 0},
            'p:last-child': {marginBottom: 0},
        },
    }
});

export const PREPARED_CART_EMAIL_INITIAL_TEMPLATE =
`
<div style="margin: 0 auto; max-width: 600px; background-color: #fff;">
    <div style="background-color: #eee; padding: 1em;">
        <h2>{{{storefront}}}</h2>
        <p>{{{sales_rep_first_name}}} {{{sales_rep_last_name}}} has shared a Prepared Cart on {{{storefront}}} for you</p>
    </div>
    <div style="padding: 1em;">
        <p>
            <a href="{{{prepared_cart_link}}}" style="color: #000; text-decoration: none;">Prepared Cart</a>
        </p>
        (link will expire in 24 hours)
    </div>
    <div style="background-color: #eee; padding: 1em; text-align: center">
        <a href="" style="color: #000; text-decoration: none;">{{{storefront}}}</a>
        <p style="font-size: 10px">This email was sent by Adaptive Catalog on behalf of {{{storefront}}}. This 
            was sent from a notification only address that does not monitor inbound emails. Please do not reply to
            this message.</p>
    </div>
</div>
`

export const QUOTE_REQUEST_EMAIL_INITIAL_TEMPLATE =
`
<div style="margin: 0 auto; max-width: 600px; background-color: #fff;">
    <div style="background-color: #eee; padding: 1em;">
        <h2>{{{storefront}}}</h2>
        <p>Thank you, {{{customer_first_name}}} {{{customer_last_name}}}! Your quote request has been received.</p>
    </div>
    <div style="padding: 1em;">
        <p>Hi {{{customer_first_name}}},</p>
        <p>We have received your quote request and {{{sales_rep_first_name}}} {{{sales_rep_last_name}}} will be in touch with you shortly.</p>
        <p>Here is a summary of your request:</p>
        <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <thead>
                <tr style="background-color: #eee;">
                    <th style="text-align: left; padding: 8px; border-bottom: 1px solid #ddd;">Item</th>
                    <th style="text-align: right; padding: 8px; border-bottom: 1px solid #ddd;">Qty</th>
                </tr>
            </thead>
            <tbody>
                {{{quote_items_table}}}
            </tbody>
        </table>
        <p style="margin-top: 1em;">If you have any questions in the meantime, please contact your sales representative:</p>
        <p>
            <strong>{{{sales_rep_first_name}}} {{{sales_rep_last_name}}}</strong><br />
            <a href="mailto:{{{sales_rep_email}}}" style="color: #000;">{{{sales_rep_email}}}</a>
        </p>
    </div>
    <div style="background-color: #eee; padding: 1em; text-align: center">
        <a href="" style="color: #000; text-decoration: none;">{{{storefront}}}</a>
        <p style="font-size: 10px">This email was sent by Adaptive Catalog on behalf of {{{storefront}}}. This
            was sent from a notification only address that does not monitor inbound emails. Please do not reply to
            this message.</p>
    </div>
</div>
`
export const SHARE_CART_EMAIL_TEMPLATE = 
`
<div style="margin: 0 auto; max-width: 600px; background-color: #fff;">
    <div style="background-color: #eee; padding: 1em;">
        <h2>{{{storefront}}}</h2>
        <p>{{{shared_by_first_name}}} {{{shared_by_last_name}}} has shared a cart with you.</p>
    </div>
    <div style="padding: 1em;">
        <p>Hi {{{customer_first_name}}},</p>
        <p><strong>{{{shared_by_first_name}}} {{{shared_by_last_name}}}</strong> has shared a cart with you on <strong>{{{storefront}}}</strong>.</p>
        {{{customer_notes}}}
        <h3 style="margin-bottom: 4px;">Cart Details</h3>
        <table style="width: 100%; font-size: 14px; margin-bottom: 1em;">
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold; width: 140px;">Cart Name</td>
                <td style="padding: 4px 0;">{{{cart_name}}}</td>
            </tr>
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold;">Description</td>
                <td style="padding: 4px 0;">{{{cart_description}}}</td>
            </tr>
        </table>
        <p style="margin-top: 1.5em;">
            <a href="{{{prepared_cart_link}}}" style="background-color: #789c2f; color: #fff; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-size: 14px;">View Shared Cart</a>
        </p>
    </div>
    <div style="background-color: #eee; padding: 1em; text-align: center">
        <a href="{{{storefront_link}}}" style="color: #000; text-decoration: none;">{{{storefront}}}</a>
        <p style="font-size: 10px">This email was sent by Adaptive Catalog on behalf of {{{storefront}}}. This
            was sent from a notification only address that does not monitor inbound emails. Please do not reply to
            this message.</p>
    </div>
</div>
`
export const QUOTE_REQUEST_SALES_REP_EMAIL_TEMPLATE =
`
<div style="margin: 0 auto; max-width: 600px; background-color: #fff;">
    <div style="background-color: #eee; padding: 1em;">
        <h2>{{{storefront}}}</h2>
        <p>A new quote has been requested by {{{customer_first_name}}} {{{customer_last_name}}}.</p>
    </div>
    <div style="padding: 1em;">
        <p>Hi {{{sales_rep_first_name}}},</p>
        <p>{{{customer_first_name}}} {{{customer_last_name}}} has submitted a quote request through <strong>{{{storefront}}}</strong> and is waiting to hear from you.</p>
        <h3 style="margin-bottom: 4px;">Customer Information</h3>
        <table style="width: 100%; font-size: 14px; margin-bottom: 1em;">
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold; width: 140px;">Name</td>
                <td style="padding: 4px 0;">{{{customer_first_name}}} {{{customer_last_name}}}</td>
            </tr>
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold;">Email</td>
                <td style="padding: 4px 0;"><a href="mailto:{{{customer_email}}}" style="color: #000;">{{{customer_email}}}</a></td>
            </tr>
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold;">Phone</td>
                <td style="padding: 4px 0;">{{{customer_phone}}}</td>
            </tr>
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold;">Company</td>
                <td style="padding: 4px 0;">{{{customer_company}}}</td>
            </tr>
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold;">Notes</td>
                <td style="padding: 4px 0;">{{{customer_notes}}}</td>
            </tr>
        </table>
        <h3 style="margin-bottom: 4px;">Requested Items</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <thead>
                <tr style="background-color: #eee;">
                    <th style="text-align: left; padding: 8px; border-bottom: 1px solid #ddd;">Item</th>
                    <th style="text-align: right; padding: 8px; border-bottom: 1px solid #ddd;">Qty</th>
                    <th style="text-align: right; padding: 8px; border-bottom: 1px solid #ddd;">List Price</th>
                </tr>
            </thead>
            <tbody>
                {{{cart_items_table}}}
            </tbody>
        </table>
        <p style="margin-top: 1.5em;">
            <a href="{{{workspace_link}}}" style="background-color: #789c2f; color: #fff; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-size: 14px;">View Quote Request</a>
        </p>
    </div>
    <div style="background-color: #eee; padding: 1em; text-align: center">
        <a href="" style="color: #000; text-decoration: none;">{{{storefront}}}</a>
        <p style="font-size: 10px">This email was sent by Adaptive Catalog on behalf of {{{storefront}}}. This
            was sent from a notification only address that does not monitor inbound emails. Please do not reply to
            this message.</p>
    </div>
</div>
`

export const PREPARED_CART_QUESTION_EMAIL_TEMPLATE =
`
<div style="margin: 0 auto; max-width: 600px; background-color: #fff;">
    <div style="background-color: #eee; padding: 1em;">
        <h2>{{{storefront}}}</h2>
        <p>{{{customer_first_name}}} {{{customer_last_name}}} has a question about their Prepared Cart.</p>
    </div>
    <div style="padding: 1em;">
        <p>Hi {{{sales_rep_first_name}}},</p>
        <p><strong>{{{customer_first_name}}} {{{customer_last_name}}}</strong> has left a message regarding the Prepared Cart you sent them on <strong>{{{storefront}}}</strong>.</p>
        <h3 style="margin-bottom: 4px;">Customer Message</h3>
        <div style="background-color: #f9f9f9; border-left: 4px solid #789c2f; padding: 12px 16px; font-size: 14px; margin-bottom: 1.5em;">
            {{{customer_message}}}
        </div>
        <h3 style="margin-bottom: 4px;">Prepared Cart</h3>
        <table style="width: 100%; border-collapse: collapse; font-size: 14px; margin-bottom: 1.5em;">
            <thead>
                <tr style="background-color: #eee;">
                    <th style="text-align: left; padding: 8px; border-bottom: 1px solid #ddd;">Item</th>
                    <th style="text-align: right; padding: 8px; border-bottom: 1px solid #ddd;">Qty</th>
                    <th style="text-align: right; padding: 8px; border-bottom: 1px solid #ddd;">Price</th>
                </tr>
            </thead>
            <tbody>
                {{{cart_items_table}}}
            </tbody>
        </table>
        <h3 style="margin-bottom: 4px;">Customer Information</h3>
        <table style="width: 100%; font-size: 14px; margin-bottom: 1.5em;">
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold; width: 140px;">Name</td>
                <td style="padding: 4px 0;">{{{customer_first_name}}} {{{customer_last_name}}}</td>
            </tr>
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold;">Email</td>
                <td style="padding: 4px 0;"><a href="mailto:{{{customer_email}}}" style="color: #000;">{{{customer_email}}}</a></td>
            </tr>
            <tr>
                <td style="padding: 4px 8px 4px 0; font-weight: bold;">Phone</td>
                <td style="padding: 4px 0;">{{{customer_phone}}}</td>
            </tr>
        </table>
        <p style="margin-top: 1.5em;">
            <a href="{{{prepared_cart_link}}}" style="background-color: #789c2f; color: #fff; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-size: 14px;">View Prepared Cart</a>
        </p>
    </div>
    <div style="background-color: #eee; padding: 1em; text-align: center">
        <a href="" style="color: #000; text-decoration: none;">{{{storefront}}}</a>
        <p style="font-size: 10px">This email was sent by Adaptive Catalog on behalf of {{{storefront}}}. This
            was sent from a notification only address that does not monitor inbound emails. Please do not reply to
            this message.</p>
    </div>
</div>
`

export const MODAL_ICON_BUTTON_STYLE: Partial<IButtonStyles> = {
    root: {
        color: theme.palette.neutralPrimary,
        marginLeft: 'auto',
        marginTop: '4px',
        marginRight: '2px',
    },
    rootHovered: {
        color: theme.palette.neutralDark,
    },
};

export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const PROCUREMENT_ACTIVITY_TYPE: {[key: string]: string} = {
    ITEMS_CREATED: 'DemandItemsCreated',
    ITEMS_UPDATED: 'DemandItemUpdated',
    ITEMS_CANCELLED: 'DemandItemsCancelled',
    ORDER_CREATED: 'OrderCreated',
    ORDER_UPDATED: 'OrderUpdated',
    ORDER_CANCELLED: 'OrderCancelled',
    ORDER_APPROVED: 'OrderApproved',
    ORDER_REJECTED: 'OrderRejected',
    ORDER_SHIPPED: 'OrderShipped',
    ORDER_SUBMITTED: 'OrderSubmitted',
    ORDER_DELIVERED: 'OrderDelivered',
};

export const ProcurementActivityDisplay = (key?: string): string => {
    switch (key) {
        case PROCUREMENT_ACTIVITY_TYPE.ITEMS_CREATED:
            return 'Demand Items Created';
        case PROCUREMENT_ACTIVITY_TYPE.ITEMS_UPDATED:
            return 'Demand Item Updated';
        case PROCUREMENT_ACTIVITY_TYPE.ITEMS_CANCELLED:
            return 'Demand Items Cancelled';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_CREATED:
            return 'Order Created';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_UPDATED:
            return 'Order Updated';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_CANCELLED:
            return 'Order Cancelled';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_APPROVED:
            return 'Order Approved';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_REJECTED:
            return 'Order Rejected';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_SHIPPED:
            return 'Order Shipped';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_SUBMITTED:
            return 'Order Submitted';
        case PROCUREMENT_ACTIVITY_TYPE.ORDER_DELIVERED:
            return 'Order Delivered';
        default:
            return key ?? 'Unknown Activity';
    }
}

export const STOREFRONT_ORDER_ACTIVITY_TYPE: {[key: string]: string} = {
    ORDER_PLACED: 'OrderPlaced',
    EMAIL_SENT: 'EmailSent',
    OPPORTUNITY_CREATED: 'OpportunityCreated',
    DEMAND_ITEMS_CREATED: 'DemandItemsCreated',
    PROCUREMENT_ORDER_CREATED: 'ProcurementOrderCreated',
    SALES_ORDER_CREATED: 'SalesOrderCreated',
    WEBHOOK_SENT: 'WebhookSent',
    STATUS_UPDATED: 'StatusUpdated',
    SHIPMENT_ADDED: 'ShipmentAdded',
    SHIPMENT_UPDATED: 'ShipmentUpdated',
    SHIPMENT_REMOVED: 'ShipmentRemoved',
};

export const StorefrontOrderActivityDisplay = (key?: string): string => {
    switch (key) {
        case STOREFRONT_ORDER_ACTIVITY_TYPE.ORDER_PLACED:
            return 'Order Placed';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.EMAIL_SENT:
            return 'Confirmation Email';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.OPPORTUNITY_CREATED:
            return 'Opportunity Creation';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.DEMAND_ITEMS_CREATED:
            return 'Procurement Demand Items';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.PROCUREMENT_ORDER_CREATED:
            return 'Procurement Order';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SALES_ORDER_CREATED:
            return 'Sales Order';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.WEBHOOK_SENT:
            return 'Webhook';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.STATUS_UPDATED:
            return 'Status Update';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SHIPMENT_ADDED:
            return 'Tracking Added';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SHIPMENT_UPDATED:
            return 'Tracking Updated';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SHIPMENT_REMOVED:
            return 'Tracking Removed';
        default:
            return key ?? 'Activity';
    }
}

export const EXTENSION_ACTIVITY_TYPE: {[key: string]: string} = {
    IMPORT_INITIALIZED: 'ImportInitialized',
    IMPORT_CALLBACK_RECEIVED: 'ImportCallbackReceived',
    IMPORT_STARTED: 'ImportStarted',
    IMPORT_FINISHED: 'ImportFinished',
    IMPORT_FAILED: 'ImportFailed',
    OPPORTUNITY_CREATED: 'OpportunityCreated',
    OPPORTUNITY_UPDATED: 'OpportunityUpdated',
    SALES_ORDER_CREATED: 'SalesOrderCreated',
    SYNC_FINISHED: 'SyncFinished',
    SYNC_FAILED: 'SyncFailed',
    SETTINGS_UPDATED: 'SettingsUpdated',
    RUN: 'Run',
    VALIDATED: 'Validated',
    EXTENSION_ERROR: 'ExtensionError',
};

export const ExtensionActivityDisplay = (key?: string): string => {
    switch (key) {
        case EXTENSION_ACTIVITY_TYPE.IMPORT_INITIALIZED:
            return 'Import Requested';
        case EXTENSION_ACTIVITY_TYPE.IMPORT_CALLBACK_RECEIVED:
            return 'Data Received';
        case EXTENSION_ACTIVITY_TYPE.IMPORT_STARTED:
            return 'Import Started';
        case EXTENSION_ACTIVITY_TYPE.IMPORT_FINISHED:
            return 'Import Finished';
        case EXTENSION_ACTIVITY_TYPE.IMPORT_FAILED:
            return 'Import Failed';
        case EXTENSION_ACTIVITY_TYPE.OPPORTUNITY_CREATED:
            return 'Opportunity Created';
        case EXTENSION_ACTIVITY_TYPE.OPPORTUNITY_UPDATED:
            return 'Opportunity Updated';
        case EXTENSION_ACTIVITY_TYPE.SALES_ORDER_CREATED:
            return 'Sales Order Created';
        case EXTENSION_ACTIVITY_TYPE.SYNC_FINISHED:
        case EXTENSION_ACTIVITY_TYPE.SYNC_FAILED:
            return 'CRM Sync';
        case EXTENSION_ACTIVITY_TYPE.SETTINGS_UPDATED:
            return 'Settings Updated';
        case EXTENSION_ACTIVITY_TYPE.RUN:
            return 'Run';
        case EXTENSION_ACTIVITY_TYPE.VALIDATED:
            return 'Validation';
        case EXTENSION_ACTIVITY_TYPE.EXTENSION_ERROR:
            return 'Extension Error';
        default:
            return key ?? 'Activity';
    }
}

export const PROCUREMENT_ORDER_STATUSES: {[key:string]: string} = {
    PENDING: 'Pending',
    SUBMITTED: 'Submitted',
    CONFIRMED: 'Confirmed',
    CREDIT_HOLD: 'CREDIT HOLD',
    IN_PROGRESS: 'In Progress',
    BACKORDERED: 'Backordered',
    PARTIAL: 'Partial',
    SHIPPED: 'Shipped',
    DELIVERED: 'Delivered',
    CANCELLED: 'Cancelled',
    COMPLETE: 'Complete',
}

export const PROCUREMENT_DEMAND_ITEM_STATUSES: {[key:string]: string} = {
    NEW: 'New',
    ORDERED: 'Ordered',
    IN_PROGRESS: 'In Progress',
    FULFILLED: 'Fulfilled',
    CANCELLED: 'Cancelled',
}
/**
 * The product levels an organization can be on, exactly as OrganizationEntitlements.ParseProductLevel
 * spells them on the API side — the strings are compared literally when deciding what a global connector
 * or template is offered to, so a casing difference silently hides the thing being gated.
 *
 * Not to be confused with the storefront tier (STOREFRONT_TIER_* in models/Settings.ts), which is a
 * separate axis that happens to share the word Enterprise.
 */
export const PRODUCT_LEVELS = ['Basic', 'Standard', 'Enterprise'] as const;
